import React, { useEffect, useState } from 'react';
import Button from 'bizcap-web-components/dist/components/button';
import useReactRouter from 'use-react-router';
import { RouteParams } from 'components/custom-route/custom-route';
import {
    useCompleteChecklistItemMutation,
    DocumentType,
    useCompleteSignerChecklistItemMutation,
    useGenerateSignerDocumentToUploadAsyncMutation,
    useGenerateCompanyDocumentToUploadAsyncMutation
} from 'generated/credit-analysis-graphql';
import { ApolloClientName } from 'apollo-client-factory';
import ChevronRightIcon from 'assets/svg/chevron-right-icon';
import { registerTagmanagerEvent, TagManagerEvent } from 'configs/trackers';
import SocialContractSampleModal from './components/social-contract-sample-modal';
import NewDocumentUploader from './components/document-uploader/new-document-uploader';
import BankStatementSampleModal from './components/bank-statement-sample-modal';
import { getKeyByValue } from 'utils/utils';
import { GetAllowedDocuments, getChecklistVariables, getSignedUrlObject } from './document-functions';
import Error from 'components/commons/error';
import NewDocumentSelector from './components/document-uploader/new-document-selector';

type AllowedDocumentsUploaderProps = {
    nextScreen(): void;
    onBackClick(): void;
};

type State = {
    allowedDocuments?: DocumentType[],
    typeGroup?: string,
}

type ContentDocument = {
    title: string,
    description?: any,
    documents: DocumentOption[],
    modal: any,
}

type AllowedDocumentsContent = {
    [type: string]: ContentDocument
}

export type DocumentOption = {
    value: string | any,
    label?: string
};

const getContent = (state: State) => {
    const content: AllowedDocumentsContent = {
        SocialContact: {
            title: 'contrato social',
            documents: GetAllowedDocuments(state.allowedDocuments),
            modal: <SocialContractSampleModal />
        },
        BankStatement: {
            title: 'extratos bancários',
            description: <div className="l-documents-screen__description">Envie no mínimo os últimos
                <b> 90 dias de extratos bancários</b> da sua <br className="hidden-sm" />
                empresa para entendermos melhor o seu faturamento. Enviar no <br className="hidden-sm" />
                formato PDF pode agilizar a sua análise de crédito</div>,
            documents: GetAllowedDocuments(state.allowedDocuments),
            modal: <BankStatementSampleModal />
        },
        PartnerIdentity: {
            title: 'documento dos sócios',
            documents: GetAllowedDocuments(state.allowedDocuments),
            modal: null
        },
        default: {
            title: 'documentos',
            documents: GetAllowedDocuments(state.allowedDocuments),
            modal: null
        },
    }
    return content[state.typeGroup] || content.default;
}

const AllowedDocumentsUploader: React.FC<AllowedDocumentsUploaderProps> = (
    { nextScreen, onBackClick, children }): JSX.Element => {
    const { match: { params: { loanApplicationId } }, history } = useReactRouter<RouteParams>();
    const [submit, setSubmit] = useState(false);
    const [buttonLoading, setButtonLoading] = useState(false);
    const [submitEnabled, setSubmitEnabled] = useState(false);
    const [hasItem, setHasItem] = useState(false);
    const [signerId, setSignerId] = useState<any>(false);
    const [documentSelected, setDocumentSelected] = useState<DocumentOption>(null);
    const [content, setContet] = useState<ContentDocument>({
        title: '',
        documents: [],
        modal: null
    });
    const [errorMsg, setErrorMsg] = useState<boolean>(false);

    const [completeChecklistItem] = useCompleteChecklistItemMutation({
        context: { clientName: ApolloClientName.CreditAnalysisApi },
    });
    const [completeSignerChecklistItem] = useCompleteSignerChecklistItemMutation({
        context: { clientName: ApolloClientName.CreditAnalysisApi },
    });
    const [getSignedUrlSigner] = useGenerateSignerDocumentToUploadAsyncMutation({
        context: { clientName: ApolloClientName.CreditAnalysisApi },
    });
    const [getSignedUrlCompany] = useGenerateCompanyDocumentToUploadAsyncMutation({
        context: { clientName: ApolloClientName.CreditAnalysisApi },
    });

    useEffect(() => {
        setContet(getContent(history.location.state));
        setSignerId((history.location.state as any).signer ? (history.location.state as any).signer.id : false);
        if ((history.location.state as any).allowedDocuments.length === 1) {
            setDocumentSelected(
                getContent(history.location.state).documents.find(
                    doc => doc.value === (history.location.state as any).allowedDocuments[0]
                ));
        }
    }, [])

    useEffect(() => {
        setSubmitEnabled((hasItem && documentSelected != null))
    }, [hasItem, documentSelected])

    const handleSubmit = async (value: string) => {

        // transform string to enum DocumentType
        const selectedType: DocumentType = DocumentType[getKeyByValue(DocumentType, value)]
        setErrorMsg(false)
        try {
            setButtonLoading(true);
            signerId
                ? await completeSignerChecklistItem(getChecklistVariables(loanApplicationId, selectedType, signerId))
                : await completeChecklistItem(getChecklistVariables(loanApplicationId, selectedType));

            nextScreen();
            registerTagmanagerEvent(TagManagerEvent.DOCUMENTS);
        } catch (error) {
            setErrorMsg(true)
            console.error('AllowedDocumentsUploader', error)
        } finally {
            setButtonLoading(false);
        }
    }

    return (
        <div className="l-documents-screen grid-container">
            <div className="l-documents-screen__content">
                {
                    !documentSelected ?
                        <>
                            <h2 className="title-3 text-primary">Escolha o Documento</h2>
                            <NewDocumentSelector
                                documentOptions={content.documents}
                                {...{ setDocumentSelected }}
                            />
                        </>
                        :
                        <>
                            <h2 className="title-3 text-primary">{documentSelected.label}</h2>
                            {content.description}
                            <NewDocumentUploader
                                className="mb-5"
                                submit={submit}
                                id="contrato_social_upload"
                                loanApplicationId={loanApplicationId}
                                documentDescription={() => content.modal}
                                onUploadComplete={() => {
                                    handleSubmit(documentSelected.value);
                                }}
                                onError={() => {
                                    setButtonLoading(false);
                                }}
                                enableSubmit={(status) => setHasItem(status)}
                                getSignedUrl={async (filename, contentType) => {
                                    if (signerId) {
                                        const { data } = await
                                            getSignedUrlSigner(getSignedUrlObject(
                                                filename,
                                                contentType,
                                                loanApplicationId,
                                                documentSelected.value,
                                                signerId
                                            ))
                                        return data.generateSignerDocumentToUploadAsync;
                                    } else {
                                        const { data } =
                                            await getSignedUrlCompany(getSignedUrlObject(
                                                filename,
                                                contentType,
                                                loanApplicationId,
                                                documentSelected.value
                                            ))
                                        return data.generateCompanyDocumentToUploadAsync;
                                    }
                                }}
                            />
                        </>
                }
                {errorMsg && <Error
                    key={'allowed-Documents-error'}
                    message={'Não foi possível enviar os arquivos.'} />
                }
                {/* if has signer return AllowedDocumentsPartner */}
                {documentSelected && children}
                <div className="l-documents-screen__actions">
                    <div>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={(documentSelected && (history.location.state as any).allowedDocuments.length > 1) ?
                                () => setDocumentSelected(null) : onBackClick}
                            disabled={buttonLoading}
                        >
                            <span>voltar</span>
                            <ChevronRightIcon />
                        </Button>
                    </div>
                    {documentSelected &&
                        <Button
                            type="submit"
                            loading={buttonLoading}
                            onClick={() => {
                                setSubmit(!submit);
                                setButtonLoading(true);
                            }}
                            disabled={buttonLoading || !submitEnabled}
                        >
                            enviar
                        </Button>
                    }
                </div>
            </div>
        </div>
    );
};

export default AllowedDocumentsUploader;
